import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Pagination,
  Snackbar,
  Alert,
  Stack,
  Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { getAjioListings, downloadAjioListingsByExcel } from '../../services/marketplaceapi';
import FileUploadModalNew from '../../components/common/FileUploadModalNew';
import FileDownloadModal from '../../components/common/FileDownloadModal';

const AjioMarketplacePage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch data on component mount and whenever page or search changes
  useEffect(() => {
    fetchListings();
  }, [page, searchTerm]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare params for API call
      const params = {
        page,
        page_size: rowsPerPage
      };

      // Add search term if present
      if (searchTerm.trim()) {
        params.search = searchTerm;
      }

      // Add cache buster
      params._t = new Date().getTime();

      const response = await getAjioListings(params);
      
      if (response.data.results) {
        setListings(response.data.results);
        setTotalCount(response.data.count);
      } else {
        // Fallback for non-paginated response
        setListings(response.data);
        setTotalCount(response.data.length);
      }
    } catch (err) {
      console.error('Error fetching Ajio listings:', err);
      setError('Failed to load Ajio listings. Please try again.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchListings();
  };

  const handleSearch = (e) => {
    // Don't trigger API call on every keystroke
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleUploadSuccess = (data) => {
    setNotification({
      open: true,
      message: `Listings uploaded successfully! Created: ${data.created}, Updated: ${data.updated}`,
      severity: 'success'
    });
    
    // Refresh the listings to show the new data
    fetchListings();
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Box>
      <Breadcrumbs items={[{ label: 'Dashboard', path: '/' }, { label: 'Ajio Marketplace' }]} />
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Ajio Listings</Typography>
        
        <Stack direction="row" spacing={2}>
          
          
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<CloudUploadIcon />} 
            onClick={() => setUploadModalOpen(true)}
          >
            Upload Listings
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<CloudDownloadIcon />} 
            onClick={() => setDownloadModalOpen(true)}
          >
            Download Listings
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            placeholder="Search listings..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Paper>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Jio Code</TableCell>
                <TableCell>EAN</TableCell>
                <TableCell>Seller Style Code</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>HSN</TableCell>
                <TableCell>MRP</TableCell>
                <TableCell>Trade Discount</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Website</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading listings...</Typography>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    <Typography color="error">{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : listings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    <Typography>No listings found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                listings.map((listing) => (
                  <TableRow key={listing.id} hover>
                    <TableCell>{listing.sku_details?.sku || listing.sku}</TableCell>
                    <TableCell>{listing.jio_code}</TableCell>
                    <TableCell>{listing.ean}</TableCell>
                    <TableCell>{listing.seller_style_code}</TableCell>
                    <TableCell>{listing.size}</TableCell>
                    <TableCell>{listing.color}</TableCell>
                    <TableCell>{listing.hsn}</TableCell>
                    <TableCell>₹{listing.mrp}</TableCell>
                    <TableCell>{listing.trade_discount}</TableCell>
                    <TableCell>{listing.company_name || listing.company}</TableCell>
                    <TableCell>
                      {listing.website_link && (
                        <Link 
                          href={listing.website_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          View <OpenInNewIcon fontSize="small" sx={{ ml: 0.5 }} />
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Paper>

      {/* Upload modal */}
      <FileUploadModalNew
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        app="marketplace"
        table="ajio"
        title="Upload Ajio Listings"
      />

      {/* Download modal */}
      <FileDownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        title="Download Ajio Listings"
        description="Upload an Excel file with JIO codes in column A starting from row 3 to generate a report of Seller SKU ID, SKU, Stock, Total Sale, Cost Price, and Month Old data."
        acceptedFileTypes=".xls,.xlsx"
        processUploadFn={downloadAjioListingsByExcel}
      />

      {/* Notification snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeNotification} 
          severity={notification.severity} 
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AjioMarketplacePage;
